import './header';
import './item-action';
import './item-filter';
import './loop-filters';
import './status';

// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';

// Import style for web component
import styles from './web-component-style.scss';

// Get translatable strings
const strings = UncannyAutomatorBackend.logs.components.logLoop.i18n;

// eslint-disable-next-line require-jsdoc
@customElement( 'uap-log-loop' )
export class AutomatorLogItemLoop extends LitElement {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	// Properties
	// The post ID of the loop
	@property( { type: Number, attribute: 'loop-id' } ) accessor id;

	// A unique code created to detect this specific loop run in this specific recipe run
	// The backend will use this to cancel/pause/resume the loop
	@property( { type: String, attribute: 'loop-process-code' } ) accessor processCode;

	// The logic of the loop filters
	@property( { type: String } ) accessor logic = 'intersection';

	// The date the loop started running
	@property( { type: String, attribute: 'start-date' } ) accessor startDate;

	// The date the loop finished running
	@property( { type: String, attribute: 'end-date' } ) accessor endDate;

	// The loop filters of this loop block
	@property( { type: Array, attribute: false } ) accessor loopFilters;

	// Information about what has to be iterated (e.g. users, posts, tokens)
	@property( { type: Object, attribute: false } ) accessor iterableExpression;

	// The items inside the loop block
	@property( { type: Array, attribute: false } ) accessor items;

	// In other items, `status` only has the `status_id`. But in this case it's an object that also has the
	// number of total and completed items
	@property( { type: Object, attribute: false } ) accessor status;

	// Information about the user selector of the loop, if needed
	@property( { type: Object, attribute: false } ) accessor runOn;

	// The difference between both start and end dates
	@property( { type: Object, attribute: false } ) accessor dateElapsed;

	// Information about when the next batch of this loop will run
	@property( { type: Object, attribute: false } ) accessor dateNextProcess;

	// The ID of the current item opened
	@property( { type: String, attribute: 'sidebar-source-id' } ) accessor sidebarSourceID;

	// This property is used to indicate to the web component that it shouldn't try to retrieve data from the server
	// and that it should use the data provided when it was created
	@property( { type: Boolean, attribute: 'import-mode' } ) accessor isImportMode = false;

	// eslint-disable-next-line require-jsdoc
	render() {
		return html`
			<div class="loop">
				${ this._templateSummary }
				${ this._templateStatus }
				${ this._templateItems }
			</div>
		`;
	}

	/**
	 * Returns the template result that contains information about the loop progress
	 *
	 * @return {TemplateResult} The loop progress data
	 */
	get _templateStatus() {
		return html`
			<uap-log-loop-status
				loop-id=${ this.id }
				loop-process-code=${ this.processCode }

				status-id=${ this.status.status_id }
				elements-completed=${ this.status.elements_completed }
				elements-total=${ this.status.elements_total }
				start-date=${ this.startDate }
				end-date=${ this.endDate }
				
				.message=${ this.status.message }

				.dateElapsed=${ this.dateElapsed }
				.dateNextProcess=${ this.dateNextProcess }

				?import-mode=${ this.isImportMode }
			></uap-log-loop-status>
		`;
	}

	/**
	 * Shows a summary of the loop, including the data that has to be iterated
	 *
	 * @return {TemplateResult} Summary of the loop block
	 */
	get _templateSummary() {
		return html`
			<div class="loop-summary">
				<uap-log-loop-header
					.iterableExpression=${ this.iterableExpression }
				></uap-log-loop-header>

				<uap-log-loop-filters
					sidebar-source-id=${ this.sidebarSourceID }
					?import-mode=${ this.isImportMode }
					.loopFilters=${ this.loopFilters }
				></uap-log-loop-filters>
			</div>
		`;
	}

	/**
	 * Returns the template result that contains all the items of the loop
	 *
	 * @return {TemplateResult} The items
	 */
	get _templateItems() {
		return html`
			<div class="loop-items">
				${ repeat(
					this.items,
					item => item.id,
					item => {
						if ( item.type === 'action' ) {
							return html`
								<uap-log-loop-action 
									item-id=${ item.id }
									item-code=${ item.code }

									integration-code=${ item.integration_code }
									
									.titleHTML=${ item.title_html }
									.fields=${ item.fields }
									.runs=${ item.runs }

									.status=${ item.status }

									?import-mode=${ this.isImportMode }

									?is-open=${ this.sidebarSourceID === `item-${ item.id }` }
								></uap-log-loop-action>
							`;
						}

						if ( item.type === 'filter' ) {
							return html`
								<uap-log-loop-action-filter
									filter-id="${ item.id }"
									logic="${ item.logic }"
									sidebar-source-id="${ this.sidebarSourceID }"

									.conditions=${ item.conditions }
									.items=${ item.items }

									?import-mode=${ this.isImportMode }
								></uap-log-loop-action-filter>
							`;
						}
					}
				) }
			</div>
		`;
	}
}